import { signalDates, rollUpDays } from '../../horizon/horizonCalendar';
import {
  NEAR_DAYS,
  attentionDayCount,
  buildHeadline,
  buildHighlightTiles,
  buildOutletRail,
  nearWindowTotals,
} from '../../horizon/horizonHighlights';
import { groupHorizonSignals } from '../../horizon/horizonSignalGroups';
import { sourceSummaryFromCells } from '../../horizon/horizonSources';
import type { HorizonBookingDay, HorizonPayload, HorizonSignal } from '../../horizon/horizonTypes';

export function horizonSignalDate(signal: HorizonSignal): string | null {
  return (
    signal.link?.serviceDate ||
    (signal.evidence.kind === 'horizonDate' ? signal.evidence.serviceDate : null)
  );
}

export function resolveHorizonScopeId(
  data: HorizonPayload,
  requestedId?: string | null,
): string | null {
  const outlets = data.outlets ?? [];
  if (requestedId && outlets.some(outlet => outlet.id === requestedId)) {
    return requestedId;
  }
  return outlets.length === 1 ? outlets[0].id : null;
}

export function buildHorizonWidgetModel(
  data: HorizonPayload,
  requestedOutletId?: string | null,
  bookingDays?: HorizonBookingDay[] | null,
) {
  const cells = data.courseMap ?? [];
  const outlets = data.outlets ?? [];
  const scopeId = resolveHorizonScopeId(data, requestedOutletId);
  const days = rollUpDays(cells, outlets, scopeId);
  const nearDays = days.slice(0, NEAR_DAYS);
  const signals = data.signals ?? [];
  const flagged = signalDates(signals, scopeId);
  const scopedSignals = scopeId
    ? signals.filter(signal => !signal.outletId || signal.outletId === scopeId)
    : signals;
  const nearDates = new Set(nearDays.map(day => day.serviceDate));
  const nearSignals = scopedSignals.filter(signal => {
    const date = horizonSignalDate(signal);
    return !date || nearDates.has(date);
  });
  const totals = nearWindowTotals(days, NEAR_DAYS);
  const openNearDates = new Set(totals.openDays.map(day => day.serviceDate));
  const attentionCount = attentionDayCount(nearSignals, totals.days.length);
  const headline = buildHeadline(totals, attentionCount, NEAR_DAYS);
  const rail =
    scopeId || outlets.length < 2
      ? null
      : buildOutletRail({
          cells,
          signals: scopedSignals,
          nearDates,
          span: NEAR_DAYS,
        });
  const tiles = buildHighlightTiles({
    totals,
    bookingDays: bookingDays ?? null,
    attentionCount,
    attentionSummary: nearSignals[0]?.insight ?? null,
    span: NEAR_DAYS,
  });
  const weekSources = sourceSummaryFromCells(cells, openNearDates, scopeId);
  const signalGroups = groupHorizonSignals(scopedSignals);
  const outletName = (id?: string | null) => {
    if (!id) return null;
    const named = outlets.find(outlet => outlet.id === id);
    if (named) return named.name;
    return cells.find(cell => cell.outletId === id)?.outletName ?? null;
  };

  return {
    cells,
    outlets,
    scopeId,
    days,
    nearDays,
    flagged,
    scopedSignals,
    nearSignals,
    totals,
    headline,
    rail,
    tiles,
    weekSources,
    signalGroups,
    outletName,
  };
}
